import { action, makeAutoObservable, makeObservable, observable } from 'mobx';
import { ModalFooter } from '../components';
import classNames from 'classnames';

class ModalStore {

    instances = [];

    constructor(rootStore) {
        makeObservable(this, {
            instances: observable,
            show: action.bound,
            hide: action.bound,
            hideAll: action.bound,
            openSuccessModal: action.bound,
            openErrorModal: action.bound,
            openWarningModal: action.bound,
            openAlert: action.bound,
            openProcessingModal: action.bound,
            closeProcessingModal: action.bound

        })
        this.rootStore = rootStore;
    }

    show(options) {
        const instance = new ModalInstance(options);
        this.instances.push(instance);
    }

    getCurrentInstance = () => {
        if(this.instances.length > 0) return this.instances.at(-1);
    };

    hide() {
        if(this.instances.length > 0) {
            this.instances.splice(-1, 1);
        }
    }

    hideAll() {
        this.instances = [];
    }

    openSuccessModal(headerTitle, msg, type = 'small') {
        this.show({
            id: 'modal-success',
            isOpen: true,
            header: headerTitle,
            onCancel: this.hide.bind(this),
            children: (
                <div className='text-center'>
                    <div key={'modal-body'} className='modal-body'>
                        <span>{msg}</span>
                    </div>
                </div>
            ),
            type: type
        });
    }

    openErrorModal(headerTitle, msg, type = 'small', actionButton = null) {
        this.show({
            id: 'modal-err',
            isOpen: true,
            header: headerTitle,
            onCancel: this.hide.bind(this),
            children: (
                <div className='text-center'>
                    <div key={'modal-body'} className='modal-body'>
                        <span>{msg}</span>
                    </div>
                    {actionButton}
                </div>
            ),
            type: type
        });
    }

    // Example for calling this function:
    openWarningModal(msg, cb = null, headerTitle = '確認', type = 'small', saveButtonText = 'OK', cancelButtonText= 'キャンセル', className = '') {
        this.show({
            id: 'modal-warning',
            isOpen: true,
            header: headerTitle,
            onCancel: this.hide.bind(this),
            children: (
                <div className={classNames('text-center', className)}>
                    <div key={'modal-body'} className='modal-body'>
                        <span style={{'whiteSpace': 'pre-line'}}>{msg}</span>
                    </div>
                    <ModalFooter key={'modal-footer'}
                        saveButtonText={saveButtonText}
                        onConfirm={() => {
                            this.hide();
                            cb && cb();
                        }}
                        cancelButtonText={cancelButtonText}
                        onCancel={this.hide.bind(this)}
                        saveButtonClass='btn-default-3'
                    />
                </div>
            ),
            type: type
        });
    }

    openAlert(msg, cb, type = 'small', saveButtonText = 'OK') {
        this.show({
            id: 'modal-alert',
            isOpen: true,
            notButtonX: true,
            header: null,
            onCancel: this.hide.bind(this),
            children: (
                <div className='text-center'>
                    <div key={'modal-body'} className='modal-body pd-t-40 pd-b-15'>
                        <span style={{'whiteSpace': 'pre-line'}}>{msg}</span>
                    </div>
                    <ModalFooter key={'modal-footer'}
                        saveButtonText={saveButtonText}
                        onConfirm={() => {
                            this.hide();
                            cb && cb();
                        }}
                        saveButtonClass='btn-default-3'
                    />
                </div>
            ),
            type: type
        });
    }

    // #17917 — "処理中です" spinner modal for the certificate-download sync path (replaces the
    // global elephant LoadingSrc for that one flow specifically, per Figma node 6152-6657). No
    // header (notButtonX) and deliberately no onCancel — react-modal only closes when something
    // calls hide()/onRequestClose, so with onCancel left unset, ESC/overlay-click do nothing and
    // the GA can't dismiss it mid-download (matches the spec's own warning not to close/logout).
    openProcessingModal(msg = '処理中です。しばらくお待ちください。') {
        this.show({
            id: 'modal-processing',
            isOpen: true,
            notButtonX: true,
            header: null,
            children: (
                <div className='text-center modal-processing-body'>
                    <div className='modal-processing-spinner' />
                    <p className='modal-processing-message'>{msg}</p>
                    <p className='modal-processing-warning'>
                        <u>※ダウンロードが完了するまで、ブラウザを閉じたり、ログアウトしたりしないでください。</u>
                    </p>
                </div>
            ),
            type: 'small'
        });
    }

    // Pops specifically the processing modal instance rather than the generic hide() (which just
    // pops whatever is on top of the stack) — safe even if something else got stacked on top of
    // it in the meantime (e.g. an error modal opened from the same failure callback).
    closeProcessingModal() {
        this.instances = this.instances.filter(i => i.id !== 'modal-processing');
    }
}

class ModalInstance {

    id = '';
    isOpen = false;
    onAfterOpen = () => {};
    onRequestClose = () => {};
    contentLabel = '';
    header = '';
    saveButtonText = '';
    cancelButtonText = '';
    saveButtonClass = '';
    onConfirm = () => {};
    onCancel = () => {};
    children = null;
    type = '';
    notButtonX = true;
    disabledCloseButton = false;

    constructor(options) {
        makeAutoObservable(this);
        this.id = options.id;
        this.isOpen = options.isOpen;
        this.onAfterOpen = options.onAfterOpen;
        this.onRequestClose = options.onRequestClose;
        this.contentLabel = options.contentLabel;
        this.header = options.header;
        this.saveButtonText = options.saveButtonText;
        this.cancelButtonText = options.cancelButtonText;
        this.saveButtonClass = options.saveButtonClass;
        this.onConfirm = options.onConfirm;
        this.onCancel = options.onCancel;
        this.children = options.children;
        this.type = options.type;
        this.notButtonX = options.notButtonX;
        this.disabledCloseButton = options.disabledCloseButton ?? false;
    }
}

export default ModalStore;